import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { mdiDelete, mdiFilterVariant } from '@mdi/js';
import { Button, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@mdi/react';
import { orange } from '@mui/material/colors';
import { defineStyles } from '../../../theme';
import { useEvent } from '../../../hooks';
import { useWithConfirmDialog } from '../../../confirmDialogs';
import { useShowInfoDialog } from '../../../infoDialogs';
import { useNotificationManager } from '../../../notificationManager';
import { useSTContext } from '../../SmartTableContext';
import { rowsSortingToSortDto } from '../../../api/helpers';
const styles = defineStyles({
    deleteButton: {
        position: 'relative',
        '& .MuiButton-endIcon': {
            position: 'absolute',
            top: 0,
            right: 3,
            color: orange['400'],
        },
    },
    filterIconContainer: {
        display: 'flex',
        justifyContent: 'center',
    },
    subtextRoot: {
        mt: 1,
        color: 'text.primary',
    },
});
export const DeleteRowsButton = () => {
    const theme = useTheme();
    const table = useSTContext();
    const notificationManager = useNotificationManager();
    const withConfirmDialog = useWithConfirmDialog();
    const showInfoDialog = useShowInfoDialog();
    const hasAnyRow = table.totalRows > 0;
    const hasSelectedRows = table.selectionCount > 0;
    const hasMaxRecords = table.rowFilterCondition?.maxRecords != null;
    const hasFilter = (table.rowFilterCondition?.andConditions.length ?? 0) > 0;
    const isFilterButton = hasSelectedRows || hasFilter || hasMaxRecords;
    const deleteRows = useEvent(async () => {
        const condition = {
            andConditions: table.rowFilterCondition?.andConditions ?? [],
            sorting: rowsSortingToSortDto(table.sorting),
            maxRecords: table.rowFilterCondition?.maxRecords,
        };
        const response = await table.api.deleteRows(table.selectedRows, condition);
        // Handle warning response (blocked rows)
        if (response && 'rowCount' in response && response.rowCount) {
            showInfoDialog({
                title: (_jsx(FormattedMessage, { id: "batchEditDialog.delete_warning", defaultMessage: "{n} samples will not be deleted, because they are included in a batch.", values: { n: response.rowCount } })),
            });
        }
        if (response && 'operationId' in response && response.operationId) {
            if (!notificationManager) {
                throw new Error('DeleteRowsButton must be used inside NotificationManagerProvider');
            }
            notificationManager.trackOperation({
                operationId: response.operationId,
                processingToast: _jsx(FormattedMessage, { id: "deleteRows.processingMessage", defaultMessage: "Processing delete..." }),
                successToast: (status) => {
                    const deleted = status.processed?.deleted ?? 0;
                    const blockedInCall = status.processed?.blockedInCall ?? 0;
                    const blockedFinalResponseCode = status.processed?.blockedFinalResponseCode ?? 0;
                    const totalBlocked = status.processed?.blocked ?? 0;
                    const notExists = status.processed?.notExists ?? 0;
                    return (_jsxs(_Fragment, { children: [_jsx("strong", { children: _jsx(FormattedMessage, { id: "deleteRows.successMessage", defaultMessage: "Delete completed successfully!" }) }), _jsx("br", {}), _jsx(FormattedMessage, { id: "deleteRows.rowsProcessed", defaultMessage: "{processed} rows processed", values: { processed: status.totalCount } }), _jsx("br", {}), _jsx("br", {}), _jsx(FormattedMessage, { id: "deleteRows.deleted", defaultMessage: "{deleted} deleted", values: { deleted } }), totalBlocked > 0 && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx(FormattedMessage, { id: "deleteRows.blocked", defaultMessage: "{blocked} blocked", values: { blocked: totalBlocked } }), blockedInCall > 0 && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("span", { style: { marginLeft: 16 }, children: _jsx(FormattedMessage, { id: "deleteRows.blockedInCall", defaultMessage: "\u2022 {blockedInCall} in call", values: { blockedInCall } }) })] })), blockedFinalResponseCode > 0 && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("span", { style: { marginLeft: 16 }, children: _jsx(FormattedMessage, { id: "deleteRows.blockedFinalResponseCode", defaultMessage: "\u2022 {blockedFinalResponseCode} final response code", values: { blockedFinalResponseCode } }) })] }))] })), notExists > 0 && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx(FormattedMessage, { id: "deleteRows.notExists", defaultMessage: "{notExists} not exists", values: { notExists } })] }))] }));
                },
                errorToast: (_jsx(FormattedMessage, { id: "deleteRows.error", defaultMessage: "Delete operation failed. Please try again." })),
                isWarning: (status) => (status.processed?.blocked ?? 0) > 0,
            });
        }
        else {
            table.resetRows();
            table.invalidateTable();
        }
    });
    let title;
    let content;
    if (hasSelectedRows) {
        title = (_jsx(FormattedMessage, { id: "rowsDelete.confirm_title.selected", defaultMessage: "Delete {n, plural, one {# selected row} other {# selected rows}}?", values: { n: table.selectionCount } }));
        content = (_jsx(Typography, { variant: "body2", sx: styles.subtextRoot, children: _jsx(FormattedMessage, { id: "rowsDelete.confirm_subtext.selected", defaultMessage: "This will delete all {highlighted} rows.", values: {
                    highlighted: (_jsx(Box, { component: "span", sx: {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                            px: 0.5,
                            borderRadius: 0.5,
                        }, children: _jsx(FormattedMessage, { id: "rowsDelete.confirm_subtext.highlighted_phrase", defaultMessage: "green-highlighted" }) })),
                } }) }));
    }
    else if (hasFilter || hasMaxRecords) {
        title = (_jsx(FormattedMessage, { id: "rowsDelete.confirm_title.filtered", defaultMessage: "Delete {n, plural, one {# filtered row} other {# filtered rows}}?", values: { n: table.totalRows } }));
    }
    else {
        title = (_jsx(FormattedMessage, { id: "rowsDelete.confirm_title.all", defaultMessage: "Delete all rows in the table ({n, number})?", values: { n: table.totalRows } }));
    }
    const handleDeleteRowsClick = withConfirmDialog({
        onConfirm: deleteRows,
        strong: !hasSelectedRows,
        title,
        content,
    });
    return (_jsx(Button, { size: "small", variant: "outlined", color: "error", disabled: !hasAnyRow, onClick: handleDeleteRowsClick, sx: styles.deleteButton, startIcon: _jsx(Box, { sx: styles.filterIconContainer, children: _jsx(Icon, { path: mdiDelete, size: 1 }) }), endIcon: isFilterButton ? _jsx(Icon, { path: mdiFilterVariant, size: 0.6 }) : null, children: _jsx(FormattedMessage, { id: "table.delete.button", defaultMessage: "Delete" }) }));
};
//# sourceMappingURL=DeleteRowsButton.js.map